import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import notify from "@/services/notify.tsx";
import { ServiceResumoDesk } from "@/services/serviceResumoDesk";
import { Controller, useForm } from "react-hook-form";
import moment from "moment";
import Select from "react-select";
import ConfigDesks from "@/services/configDesks";
import ImageGeneric from "@/assets/images/lg.png";
import { useLayout } from "@/contexts/UseLayout";
import debounce from "lodash.debounce";
import { TarefaView } from "@/components/MenuUtilidades/Tarefas/components/TarefaView";
import ReactQuill from "react-quill";
import {
  ClipboardPlus,
  Expand,
  Filter,
  Plus,
  PlusCircle,
  Trash2,
  X,
} from "lucide-react";
import { ModalGeneric } from "@/components/utils/ModalGeneric";
import { useTranslation } from "react-i18next";
import TemPermissao from "@/components/utils/NiveisDeAcesso";
import { IconCriarNovaTarefa, IconDelete, IconEdit } from "./Icons";
import {
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AddTask } from "@/components/MenuUtilidades/Tarefas/components/FormTarefas";
import CardTarefa from "@/components/MenuUtilidades/Tarefas/components/CardTarefa";
import { TarefaRapidaDropdown } from "@/components/Tarefas/TarefaRapidaDropdown";
import ModalExpansao from "@/components/MenuUtilidades/Tarefas/ModalExpansao/ModalExpansao";
import DatePicker, { registerLocale } from "react-datepicker";
import { ptBR } from "date-fns/locale";
import { format } from "date-fns";
import { safeJsonParse, toArray } from "@/utils/safeJson";

registerLocale("pt-BR", ptBR);

import {
  filtros,
  type FiltrosProps,
} from "@/components/MenuUtilidades/Tarefas/Mock";
import { NavLink } from "react-router-dom";
import { IconClose } from "@/assets/images/Icons";
import { buildTaskStatusPayload } from "@/utils/taskStatusPayload";

type Variant = "desk" | "jornada" | "sidebar";
const SIDEBAR_TASKS_PER_PAGE = 15;

function updateTaskStatusInList(tasks: any[], taskId: any, status: number) {
  return tasks.map((task: any) =>
    task?.id === taskId ? { ...task, status } : task,
  );
}

function updateTaskStatusInQueryData(oldData: any, taskId: any, status: number) {
  if (!oldData) return oldData;

  if (Array.isArray(oldData?.pages)) {
    return {
      ...oldData,
      pages: oldData.pages.map((page: any) =>
        updateTaskStatusInQueryData(page, taskId, status),
      ),
    };
  }

  if (Array.isArray(oldData)) {
    return updateTaskStatusInList(oldData, taskId, status);
  }

  if (Array.isArray(oldData?.data?.data)) {
    return {
      ...oldData,
      data: {
        ...oldData.data,
        data: updateTaskStatusInList(oldData.data.data, taskId, status),
      },
    };
  }

  if (Array.isArray(oldData?.data)) {
    return {
      ...oldData,
      data: updateTaskStatusInList(oldData.data, taskId, status),
    };
  }

  return oldData;
}

function getTasksTotalFromResponse(response: any) {
  const body = response?.data ?? response;
  const total =
    body?.qtdRegistros ??
    body?.qtd ??
    body?.total ??
    body?.meta?.total ??
    body?.pagination?.total;

  return Number.isFinite(Number(total)) ? Number(total) : null;
}

function sortTasksByDeliveryDate(tasks: any[]) {
  return [...tasks].sort((a: any, b: any) => {
    const aTimestamp = moment(a?.date).valueOf();
    const bTimestamp = moment(b?.date).valueOf();
    const aDate = Number.isFinite(aTimestamp)
      ? aTimestamp
      : Number.POSITIVE_INFINITY;
    const bDate = Number.isFinite(bTimestamp)
      ? bTimestamp
      : Number.POSITIVE_INFINITY;
    const dateDifference = aDate - bDate;

    return dateDifference || Number(b?.id || 0) - Number(a?.id || 0);
  });
}

export interface TarefasProps {
  /** Entidade vinculada (desk / reunion / prospecting / pagamento / campanha / cronograma). */
  data?: any;
  /** Tipo da entidade: "desk" | "reunion" | "prospecting" | "pagamento" | "campanha" | "cronograma" */
  type?: string;
  /** Lista de departamentos disponíveis para o seletor. */
  departamentos?: any[];
  /** ID do departamento atualmente selecionado. */
  departamentoActive?: number;
  /** Quando true, renderiza apenas o botão compacto (sem listar tarefas). */
  viewDesk?: boolean;
  variant?: Variant;
}

const CustomDateTimeInput = forwardRef(
  ({ value, onClick, onChange, placeholder, onKeyDown }: any, ref: any) => {
    const [inputValue, setInputValue] = useState(value || "");
    const isTyping = useRef(false);

    useEffect(() => {
      if (!isTyping.current) setInputValue(value || "");
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, "").slice(0, 12);
      let formatted = digits;
      if (digits.length > 8)
        formatted =
          digits.slice(0, 2) +
          "/" +
          digits.slice(2, 4) +
          "/" +
          digits.slice(4, 8) +
          " " +
          digits.slice(8, 10) +
          ":" +
          digits.slice(10);
      else if (digits.length > 4)
        formatted =
          digits.slice(0, 2) + "/" + digits.slice(2, 4) + "/" + digits.slice(4);
      else if (digits.length > 2)
        formatted = digits.slice(0, 2) + "/" + digits.slice(2);

      isTyping.current = true;
      setInputValue(formatted);
      if (digits.length === 12) {
        isTyping.current = false;
        e.target.value = formatted;
        onChange(e);
      }
    };

    return (
      <input
        ref={ref}
        value={inputValue}
        onClick={onClick}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onBlur={() => {
          isTyping.current = false;
        }}
        placeholder={placeholder ?? "dd/MM/yyyy HH:mm"}
        className="py-2 px-3 bg-cinza-100 rounded-lg placeholder-cinza-200 w-full"
      />
    );
  },
);

CustomDateTimeInput.displayName = "CustomDateTimeInput";

export function Tarefas({
  data,
  type,
  departamentos = [],
  departamentoActive,
  viewDesk = false,
  variant,
}: TarefasProps) {
  const resolvedVariant: Variant = variant ?? (data ? "desk" : "sidebar");

  if (resolvedVariant === "sidebar") {
    return <TarefasSidebar />;
  }

  return (
    <TarefasEntityLinked
      data={data}
      type={type}
      departamentos={departamentos}
      departamentoActive={departamentoActive}
      viewDesk={viewDesk}
      variant={resolvedVariant}
    />
  );
}

interface EntityLinkedProps {
  data: any;
  type: any;
  departamentos: any[];
  departamentoActive: any;
  viewDesk: boolean;
  variant: "desk" | "jornada";
}

function TarefasEntityLinked({
  data,
  type,
  departamentos,
  departamentoActive,
  viewDesk,
  variant,
}: EntityLinkedProps) {
  const { t } = useTranslation();
  const context = useLayout();

  // desk mostra subtarefas, prioridade, upload de arquivo e follow-up
  const showAdvancedFeatures = variant !== "jornada";
  const permissionModule =
    type === "campanha"
      ? "campanhas"
      : type === "cronograma"
        ? "tarefas"
        : "desks";

  const [newTask, setNewTask] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingResponsavel, setLoadingResponsavel] = useState(false);
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [tasks, setTasks] = useState<any>([]);
  const [users, setUsers] = useState<any>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [responsavelEdit, setResponsavelEdit] = useState({
    value: "",
    label: "",
  });
  const [taskSelected, setTaskSelected] = useState<any>(null);
  const [subTasks, setSubTasks] = useState<any[]>([]);
  const [showSubTaskForm, setShowSubTaskForm] = useState(false);

  const optionsPriority = [
    { value: 1, label: t("tasks.priority.high") },
    { value: 2, label: t("tasks.priority.medium") },
    { value: 3, label: t("tasks.priority.low") },
  ];

  const { register, handleSubmit, reset, setValue, watch, control } = useForm();
  const taskType = type === "recebimento" ? "pagamento" : type;

  useEffect(() => {
    getTasks();
  }, [data, departamentoActive, taskType]);

  async function getTasks() {
    try {
      const { token } = safeJsonParse<{ token?: string }>(
        localStorage.getItem("token"),
        {},
      );
      if (!token) return;
      setLoading(true);
      const res = await ServiceResumoDesk.listTasks({
        type: taskType,
        id: data.id,
        id_departamento: departamentoActive,
      });
      if (res?.status === 200) setTasks(res.data);
    } catch {
      toast.error(t("tasks.messages.fetchError"));
    } finally {
      setLoading(false);
    }
  }

  const removeNullUndefined = (obj: any) =>
    Object.fromEntries(
      Object.entries(obj).filter(
        ([, value]) => value !== null && value !== undefined,
      ),
    );

  const onSubmit = async (payload: any) => {
    const toastId = notify.loading("Salvando tarefa...");
    try {
      setIsSubmiting(true);

      // Vincula ao id da entidade conforme o tipo
      if (taskType === "desk") payload.id_desk = data.id;
      if (taskType === "reunion") payload.id_reunion = data.id;
      if (taskType === "prospecting") payload.id_prospecting = data.id;
      if (taskType === "pagamento") payload.id_pagamento = data.id;
      if (taskType === "campanha") payload.id_campanha = data.id;
      if (taskType === "cronograma") payload.id_social_media_post = data.id;

      payload.type = taskType;

      if (showAdvancedFeatures) {
        payload.priority = payload?.priority?.value;
        payload.subTarefa = JSON.stringify(subTasks);
      }

      if (!isEdit) {
        payload.id_departamento =
          taskType === "cronograma" ? undefined : departamentoActive;
        payload.status = 1;
      }

      const cleanedPayload: any = removeNullUndefined(payload);

      const formData = new FormData();

      for (const key in cleanedPayload) {
        if (cleanedPayload[key] !== undefined && cleanedPayload[key] !== null) {
          if (key === "file_id" && cleanedPayload[key]?.[0]) {
            formData.append(key, cleanedPayload[key][0]);
          } else {
            formData.append(key, cleanedPayload[key]);
          }
        }
      }

      let res;
      if (isEdit) {
        res = await ServiceResumoDesk.editTasks(formData, cleanedPayload.id);
      } else {
        res = await ServiceResumoDesk.addTasks(formData);
      }

      if (res?.status === 200) {
        notify.update(
          toastId,
          "success",
          isEdit ? "Tarefa atualizada!" : "Tarefa criada!",
        );
        getTasks();
        setNewTask(false);
        setSubTasks([]);
        reset();
      }
    } catch (error: any) {
      notify.update(
        toastId,
        "error",
        error?.response?.data?.msg ||
          error?.response?.data?.error ||
          "Erro ao salvar tarefa",
      );
    } finally {
      setIsSubmiting(false);
      setIsEdit(false);
    }
  };

  const addSubTask = () => {
    const title = watch("subtarefa_title");
    const description = watch("subtarefa_description");
    const responsavel = watch("subtarefa_responsavel");

    if (!title || !responsavel) {
      toast.error(t("tasks.messages.subtaskRequired"));
      return;
    }

    setSubTasks((prev) => [
      ...prev,
      {
        title,
        description: description || "",
        status: 1,
        id_responsavel: responsavel.value,
        responsavel_name: responsavel.label,
      },
    ]);
    setValue("subtarefa_title", "");
    setValue("subtarefa_description", "");
    setValue("subtarefa_responsavel", null);
    setShowSubTaskForm(false);
  };

  const removeSubTask = (index: number) => {
    setSubTasks((prev) => prev.filter((_, i) => i !== index));
  };

  function belongsToActiveDepartamento(item: any) {
    if (departamentoActive === undefined || departamentoActive === null) {
      return true;
    }
    if (Number(departamentoActive) === 0) return true;
    return String(item?.id_departamento) === String(departamentoActive);
  }

  async function checkedTask(task: any, status: number) {
    const previousTasks = tasks;

    try {
      setTasks((current: any) =>
        updateTaskStatusInQueryData(current, task.id, status),
      );
      const res = await ServiceResumoDesk.editTasks(
        buildTaskStatusPayload(status, task),
        task.id,
      );
      if (res?.status !== 200) throw new Error("Erro ao editar tarefa");

      void getTasks();
    } catch {
      setTasks(previousTasks);
      toast.error(t("tasks.messages.editError"));
    }
  }

  const taskFiltred = [...toArray<any>(tasks?.data)]
    .filter(belongsToActiveDepartamento)
    .sort((a: any, b: any) => {
      if (a.status === 2 && b.status !== 2) return 1;
      if (a.status !== 2 && b.status === 2) return -1;
      return 0;
    });

  const loadUsers = useCallback(
    debounce(async (inputValue: string) => {
      if (!inputValue) {
        setUsers([]);
        return;
      }
      setLoadingResponsavel(true);
      const [goOn] = await Promise.all([
        ConfigDesks.getContatosBusca(1, 10, inputValue),
      ]);

      const allOk = [goOn].every((r) => r?.status === 200);

      if (allOk) {
        const combined = [...toArray<any>(goOn?.data?.data)];

        const userLogged = context?.userLogged?.data;
        if (userLogged?.type_user_id === 5) {
          combined.push({
            name: userLogged.name,
            last_name: userLogged.last_name,
            id: userLogged.id,
            filePath: userLogged.filePath,
          });
        }
        setUsers(combined);
      }

      setLoadingResponsavel(false);
    }, 300),
    [],
  );

  const handleInputChangeUsers = (value: string) => {
    loadUsers(value);
    return value;
  };

  async function deleteDesk(id: any) {
    try {
      const res = await ServiceResumoDesk.delTasks(id);
      if (res?.status === 200) getTasks();
    } catch {
      toast.error(t("tasks.messages.editError"));
    }
  }

  async function editTask(task: any) {
    console.log("testeTask", task);

    setResponsavelEdit({
      value: task.id_responsavel,
      label: task.responsavel.name,
    });
    setNewTask(true);
    setValue("title", task.title);
    setValue("description", task.description);
    setValue("date", moment.utc(task.date).format("YYYY-MM-DDTHH:mm"));
    setValue("id_responsavel", task.id_responsavel);
    setValue("id_departamento", task.id_departamento);
    setValue("status", task.status);
    setValue("id", task.id);
    setValue("is_public", task.is_public);
    if (showAdvancedFeatures) {
      setValue(
        "priority",
        optionsPriority.filter((item) => item.value === task.priority),
      );
    }
    setIsEdit(true);
  }

  function formatarData(date: any) {
    const hoje = moment().format("YYYY-MM-DD");
    const amanha = moment().add(1, "day").format("YYYY-MM-DD");
    if (moment(date).isSame(hoje, "day")) return t("tasks.labels.today");
    if (moment(date).isSame(amanha, "day")) return t("tasks.labels.tomorrow");
    return moment(date).format("DD/MM");
  }

  function getUserById(id: any) {
    return users.find((user: any) => user.id === id) || null;
  }

  function formatUserData(list: any[]) {
    return list.map((user) => ({
      label: `${(user.name || "").trim()} ${(user.last_name || "").trim()}`,
      value: user.id,
      image: user.filePath,
    }));
  }

  function getNomeDepartamentoById(id: number) {
    const item = departamentos.find((el) => el.id === id);
    return item ? item.nome : t("tasks.labels.general");
  }

  return (
    <div>
      {viewDesk ? (
        <button
          className="p-3 bg-azul-400/20 hover:bg-azul-400 text-azul-400 hover:text-white duration-300 disabled:cursor-not-allowed"
          onClick={() => setNewTask(true)}
        >
          <ClipboardPlus size={20} />
        </button>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row flex-wrap gap-2 items-stretch">
            <button
              className="bg-azul-400 hover:bg-azul-400/80 duration-300 flex items-center justify-center text-white gap-1 py-2 px-4 rounded-3xl text-sm whitespace-nowrap flex-1"
              onClick={() => setNewTask(true)}
            >
              <IconCriarNovaTarefa /> {t("tasks.buttons.createNew")}
            </button>

            <TarefaRapidaDropdown
              type={type}
              data={data}
              departamentoActive={departamentoActive ?? 0}
              onTaskCreated={getTasks}
            />
          </div>

          <div className="space-y-3">
            <div className="text-cinza-200 text-sm flex items-center gap-2 pt-4">
              <span className="w-24">{t("tasks.labels.tasks")}</span>
              <div className="bg-cinza-100 h-[1px] w-full" />
            </div>

            {loading &&
              Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={index}
                  className="bg-[#EDEDED] rounded-3xl px-6 py-4 flex justify-between items-center gap-4 animate-pulse"
                >
                  <div className="w-6 h-6 bg-gray-300 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-300 rounded w-1/2 mb-2" />
                    <div className="h-3 bg-gray-300 rounded w-3/4" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex gap-1 text-xs">
                      <span className="bg-gray-300 h-6 w-12 rounded-2xl" />
                      <span className="bg-gray-300 h-6 w-12 rounded-2xl" />
                    </div>
                    <p className="bg-gray-300 h-6 w-20 rounded-2xl" />
                  </div>
                </div>
              ))}

            {taskFiltred?.map((e: any) => (
              <div
                className="bg-[#EDEDED] rounded-3xl px-6 py-4 relative"
                key={e.id}
              >
                <div className="flex justify-between w-full text-sm pb-2">
                  <b
                    onClick={() => setTaskSelected(e)}
                    className="cursor-pointer"
                  >
                    {e.title}
                  </b>
                  <div className="flex gap-3">
                    <span className="bg-white px-3 py-px rounded-2xl text-xs text-azul-400">
                      {getNomeDepartamentoById(e.id_departamento)}
                    </span>
                    {TemPermissao(context.userLogged, permissionModule, "editar") && (
                      <button
                        type="button"
                        className="text-azul-400 duration-300"
                        onClick={() => editTask(e)}
                      >
                        <IconEdit />
                      </button>
                    )}
                    {TemPermissao(context.userLogged, permissionModule, "remover") && (
                      <button
                        type="button"
                        className="text-azul-400 hover:text-red-500 duration-300"
                        onClick={() => deleteDesk(e.id)}
                      >
                        <IconDelete />
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex gap-4 pb-2">
                    <button
                      type="button"
                      onClick={() => checkedTask(e, e.status === 1 ? 2 : 1)}
                    >
                      <svg
                        width="17"
                        height="17"
                        viewBox="0 0 17 17"
                        fill={e.status === 1 ? "none" : "#153DCE"}
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="8.5"
                          cy="8.5"
                          r="8"
                          stroke={e.status === 1 ? "black" : "#153DCE"}
                        />
                      </svg>
                    </button>
                    <div
                      className={`text-xs linkColor max-h-24 line-clamp-2 transition-all ${
                        e.status === 2 ? "line-through" : ""
                      }`}
                      dangerouslySetInnerHTML={{
                        __html:
                          e.description ||
                          `<p>${t("tasks.labels.noObjective")}</p>`,
                      }}
                    />
                  </div>

                  <div className="flex justify-between gap-1">
                    <div className="flex gap-1 text-xs">
                      <span className="bg-white px-3 py-1 rounded-2xl text-azul-400">
                        {formatarData(e.date)}
                      </span>
                      <span className="bg-white px-3 py-1 rounded-2xl text-azul-400">
                        {moment.utc(e.date).format("HH:mm")}
                      </span>
                    </div>
                    <div className="relative">
                      <img
                        src={
                          getUserById(e.id_responsavel)?.filePath ||
                          ImageGeneric
                        }
                        alt={e.responsavel?.name}
                        className="w-6 h-6 rounded-full absolute -left-2"
                      />
                      <p className="bg-white pl-6 pr-4 py-1 rounded-2xl text-xs font-medium">
                        {e.responsavel?.name}
                      </p>
                    </div>
                  </div>
                </div>

                {taskSelected?.id === e.id && (
                  <TarefaView
                    task={taskSelected}
                    openEdit={editTask}
                    getTasks={getTasks}
                    setTaskSelected={setTaskSelected}
                  />
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {newTask
        ? createPortal(
          <div className="fixed inset-0 z-[80] flex items-center justify-center overflow-hidden p-4">
            <div className="absolute inset-0 bg-black/20" />
            <form
              className="relative z-10 w-full max-w-md rounded-3xl bg-white p-7"
              onSubmit={handleSubmit(onSubmit)}
            >
              <button
                type="button"
                className="absolute -top-4 right-0 p-1 bg-azul-400 rounded-full text-white duration-300"
                onClick={() => setNewTask(false)}
                title={t("common:close")}
              >
                <IconClose />
              </button>
              <span className="absolute -top-4 left-0 bg-azul-400 text-white text-sm px-5 py-1 rounded-2xl">
                {isEdit
                  ? getNomeDepartamentoById(watch("id_departamento"))
                  : getNomeDepartamentoById(departamentoActive)}
              </span>

              <div className="flex max-h-[calc(100dvh-5.5rem)] flex-col gap-2 overflow-y-auto">
                <input
                  type="text"
                  placeholder={t("tasks.fields.title")}
                  className="bg-cinza-100 rounded-xl text-center py-1"
                  required
                  {...register("title")}
                />

                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <ReactQuill
                      {...field}
                      className="bg-white w-full h-32 mb-20"
                      theme="snow"
                    />
                  )}
                />

                {showAdvancedFeatures && (
                  <Select
                    placeholder={t("tasks.fields.priority")}
                    className="text-center col-span-2"
                    {...register("priority")}
                    onChange={(value: any) => setValue("priority", value)}
                    value={watch("priority")}
                    options={optionsPriority}
                  />
                )}

                <Select
                  onChange={(value: any) =>
                    setValue("id_responsavel", value?.value)
                  }
                  placeholder={t("tasks.fields.responsible")}
                  required
                  onInputChange={handleInputChangeUsers}
                  defaultValue={responsavelEdit}
                  isLoading={loadingResponsavel}
                  options={formatUserData(users)}
                  noOptionsMessage={() => t("tasks.messages.typeToSearch")}
                  formatOptionLabel={(user: any) => (
                    <div className="flex gap-2 items-start">
                      <img
                        className="w-6 h-6 rounded-full"
                        src={user.image ?? ImageGeneric}
                        alt={user.label}
                      />
                      <span>{user.label}</span>
                    </div>
                  )}
                />

                {showAdvancedFeatures && (
                  <div className="col-span-2">
                    <input type="file" {...register("file_id")} />
                  </div>
                )}

                {showAdvancedFeatures && (
                  <div className="col-span-2">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-sm font-medium">
                        {t("tasks.labels.subtasks")}
                      </h4>
                      <button
                        type="button"
                        className="text-azul-400 hover:text-azul-600"
                        onClick={() => setShowSubTaskForm(!showSubTaskForm)}
                      >
                        <PlusCircle size={18} />
                      </button>
                    </div>

                    {subTasks.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {subTasks.map((subTask, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-center bg-cinza-50 p-2 rounded-lg text-xs"
                          >
                            <div className="flex-1">
                              <p className="font-medium">{subTask.title}</p>
                              <p className="text-cinza-300">
                                {subTask.responsavel_name}
                              </p>
                            </div>
                            <button
                              type="button"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => removeSubTask(index)}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {showSubTaskForm && (
                      <ModalGeneric
                        onClose={() => setShowSubTaskForm(false)}
                        size="max-w-md w-full"
                        overlayClassName="z-[90]"
                      >
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder={t("tasks.fields.subtaskTitle")}
                            className="bg-cinza-100 rounded-lg py-2 px-3 w-full text-sm"
                            {...register("subtarefa_title")}
                          />
                          <textarea
                            placeholder={t("tasks.fields.subtaskDescription")}
                            className="bg-cinza-100 rounded-lg py-2 px-3 w-full text-sm h-16 resize-none"
                            {...register("subtarefa_description")}
                          />
                          <Select
                            placeholder={t("tasks.fields.responsible")}
                            className="text-sm"
                            onChange={(value: any) =>
                              setValue("subtarefa_responsavel", value)
                            }
                            onInputChange={handleInputChangeUsers}
                            isLoading={loadingResponsavel}
                            value={watch("subtarefa_responsavel")}
                            options={formatUserData(users)}
                            noOptionsMessage={() =>
                              t("tasks.messages.typeToSearch")
                            }
                            formatOptionLabel={(user: any) => (
                              <div className="flex gap-2 items-center">
                                <img
                                  className="w-5 h-5 rounded-full"
                                  src={user.image ?? ImageGeneric}
                                  alt={user.label}
                                />
                                <span className="text-sm">{user.label}</span>
                              </div>
                            )}
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              className="bg-azul-400 text-white px-3 py-1 rounded-lg text-sm hover:bg-azul-500"
                              onClick={addSubTask}
                            >
                              {t("tasks.buttons.add")}
                            </button>
                            <button
                              type="button"
                              className="bg-cinza-300 text-white px-3 py-1 rounded-lg text-sm hover:bg-cinza-400"
                              onClick={() => setShowSubTaskForm(false)}
                            >
                              {t("tasks.buttons.cancel")}
                            </button>
                          </div>
                        </div>
                      </ModalGeneric>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <Controller
                    name="date"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <DatePicker
                        selected={field.value ? new Date(field.value) : null}
                        onChange={(date: Date | null) =>
                          field.onChange(
                            date ? format(date, "yyyy-MM-dd'T'HH:mm") : "",
                          )
                        }
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={30}
                        dateFormat="dd/MM/yyyy HH:mm"
                        locale="pt-BR"
                        placeholderText="dd/mm/yyyy --:--"
                        customInput={<CustomDateTimeInput />}
                        wrapperClassName="w-full"
                      />
                    )}
                  />
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="w-5 h-5"
                      {...register("is_public")}
                      defaultChecked={!isEdit}
                    />
                    <span>{t("tasks.fields.public")}</span>
                  </label>
                </div>

                <button
                  className="bg-azul-400 py-1 rounded-xl text-white max-w-xs w-full mx-auto"
                  type="submit"
                  disabled={isSubmiting}
                >
                  {isSubmiting
                    ? t("tasks.buttons.creating")
                    : t("tasks.buttons.create")}
                </button>
              </div>
            </form>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal: sidebar (tarefas pessoais com filtros)
// ─────────────────────────────────────────────────────────────────────────────

function TarefasSidebar() {
  const { t } = useTranslation();
  const context = useLayout();
  const [tarefaSelecionada, setTarefaSelecionada] = useState<any>(null);
  const [openAddTarefa, setOpenAddTarefa] = useState<any>(null);
  const queryClient = useQueryClient();
  const [openExpansao, setOpenExpansao] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [payload, setPayload] = useState<any>({ period: "hoje" });
  const [filtroAtivo, setFiltroAtivo] = useState<any>("hoje");
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState<any>([]);
  const isPaginatedFilter = !["hoje", "amanha", "semana"].includes(
    filtroAtivo,
  );
  const requestPayload = isPaginatedFilter
    ? { ...payload, page: currentPage, perPage: SIDEBAR_TASKS_PER_PAGE }
    : payload;
  const tarefasQueryKey = ["tarefas", requestPayload];

  const { isPending, isFetching, data, refetch } = useQuery({
    queryKey: tarefasQueryKey,
    queryFn: async () => await ServiceResumoDesk.myTasks(requestPayload),
    retry: 3,
    staleTime: 1000 * 60 * 5,
  });

  const responseTasks = toArray<any>(data?.data?.data);
  const apiTasksTotal = getTasksTotalFromResponse(data);
  const hasServerPagination = apiTasksTotal !== null;
  const tasksTotal = apiTasksTotal ?? responseTasks.length;
  const lastPage = Math.max(
    Number(data?.data?.lastPage) ||
      Math.ceil(tasksTotal / SIDEBAR_TASKS_PER_PAGE),
    1,
  );

  useEffect(() => {
    if (data && isPaginatedFilter && currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [currentPage, data, isPaginatedFilter, lastPage]);

  useEffect(() => {
    localStorage.setItem(
      "qtd_tarefas",
      JSON.stringify(
        toArray<any>(data?.data?.data).filter((e: any) => e?.status === 1)
          .length,
      ),
    );
  }, [data]);

  const loadUsers = useCallback(
    debounce(async (inputValue: string) => {
      if (!inputValue) {
        setUsers([]);
        return;
      }
      setLoadingUsers(true);
      const res = await ConfigDesks.getContatosBusca(1, 10, inputValue);
      setUsers(toArray<any>(res?.data?.data));
      setLoadingUsers(false);
    }, 300),
    [],
  );

  const handleInputChangeUsers = (value: string) => {
    loadUsers(value);
    return value;
  };

  const handleFiltroClick = (value: string) => {
    setCurrentPage(1);
    setFiltroAtivo(value);
    if (["hoje", "amanha", "semana"].includes(value)) {
      setPayload({ period: value });
    } else if (value === "pendentes") {
      setPayload({ pendentes: true });
    } else if (value === "emAndamento") {
      setPayload({ emAndamento: true });
    } else if (value === "concluidas") {
      setPayload({ concluidas: true });
    } else if (value === "reembolso") {
      setPayload({ typeTarefa: 2 });
    }
  };

  async function checkedTask(task: any) {
    let status = task.status;
    let previousQueries: any[] = [];

    try {
      if (
        context.userLogged.data.id !== task.id_responsavel &&
        context.userLogged.data.id !== task.id_user
      ) {
        toast.error(t("tasks.errors.only_responsible_organizer"));
        return;
      }
      status = task.status === 1 || task.status === 3 ? 2 : 1;
      previousQueries = queryClient.getQueriesData({
        queryKey: ["tarefas"],
      });

      await queryClient.cancelQueries({ queryKey: ["tarefas"] });
      queryClient.setQueriesData({ queryKey: ["tarefas"] }, (oldData: any) =>
        updateTaskStatusInQueryData(oldData, task.id, status),
      );
      setTarefaSelecionada((selectedTask: any) =>
        selectedTask?.id === task.id
          ? { ...selectedTask, status }
          : selectedTask,
      );

      const res = await ServiceResumoDesk.editTasks(
        buildTaskStatusPayload(status, task),
        task.id,
      );
      if (res?.status !== 200) throw new Error("Erro ao editar tarefa");

      if (isPaginatedFilter) setCurrentPage(1);
      void queryClient.invalidateQueries({ queryKey: ["tarefas"] });
    } catch {
      previousQueries?.forEach(([queryKey, previousData]) => {
        queryClient.setQueryData(queryKey, previousData);
      });
      setTarefaSelecionada((selectedTask: any) =>
        selectedTask?.id === task.id
          ? { ...selectedTask, status: task.status }
          : selectedTask,
      );
      toast.error(t("tasks.errors.edit_task"));
    }
  }

  const reloadTasks = useCallback(() => {
    if (isPaginatedFilter) {
      setCurrentPage(1);
      void queryClient.invalidateQueries({ queryKey: ["tarefas"] });
      return;
    }

    void refetch();
  }, [isPaginatedFilter, queryClient, refetch]);

  const orderedResponseTasks =
    isPaginatedFilter && !hasServerPagination
      ? sortTasksByDeliveryDate(responseTasks)
      : responseTasks;
  const tarefas =
    isPaginatedFilter && !hasServerPagination
      ? orderedResponseTasks.slice(
          (currentPage - 1) * SIDEBAR_TASKS_PER_PAGE,
          currentPage * SIDEBAR_TASKS_PER_PAGE,
        )
      : orderedResponseTasks;
  const isLoadingTasks = isPending;
  const isReimbursementFilter = filtroAtivo === "reembolso";
  const reimbursementLabel =
    filtros(t).find((item) => item.value === "reembolso")?.label ??
    "Reembolso";

  const activeTasks = tarefas?.filter((task: any) => task.status === 1);
  if (!isPaginatedFilter) {
    activeTasks.sort((a: any, b: any) => moment(a.date).diff(moment(b.date)));
  }

  const emAndamentoTasks = tarefas?.filter((task: any) => task.status === 3);
  if (!isPaginatedFilter) {
    emAndamentoTasks.sort((a: any, b: any) =>
      moment(a.date).diff(moment(b.date)),
    );
  }

  const filteredCompletedTasks = tarefas?.filter(
    (task: any) => task.status === 2,
  );
  const tarefasCompletas = isPaginatedFilter
    ? filteredCompletedTasks
    : sortTasksByDeliveryDate(filteredCompletedTasks);

  function openEdit(task: any) {
    setOpenAddTarefa(true);
    setTarefaSelecionada(task);
  }

  function formatUserData(list: any[]) {
    return list.map((user) => ({
      label: `${(user.name || "").trim()} ${(user.last_name || "").trim()}`,
      value: user.id,
      image: user.filePath,
    }));
  }

  return (
    <>
      <div className="flex flex-col h-full">
        <div className="flex flex-col">
          <div className="flex items-center gap-2 lg:gap-5">
            <span className="text-base font-medium uppercase">
              {t("menu.tasks")}
            </span>
            <button
              type="button"
              className="transition-all p-1 rounded-full border border-[#153DCE] hover:scale-105 hover:border-[#153DCE]"
              onClick={() => setOpenAddTarefa(true)}
            >
              <Plus size={15} color="#153DCE" />
            </button>
            <NavLink
              to={`/tarefasExpansao`}
              className="transition-all p-1 rounded-full border border-[#153DCE] hover:scale-105 hover:border-[#153DCE] hidden lg:flex"
              onClick={() => {
                setPayload({ period: "hoje" });
                setCurrentPage(1);
                setOpenFilters(false);
                setFiltroAtivo("hoje");
              }}
            >
              <Expand size={15} color="#153DCE" />
            </NavLink>
          </div>
        </div>

        {openAddTarefa && (
          <AddTask
            task={tarefaSelecionada}
            setDataTask={setTarefaSelecionada}
            close={setOpenAddTarefa}
            reload={reloadTasks}
          />
        )}

        <div className="flex justify-evenly sm:justify-between flex-wrap gap-1 my-3">
          {filtros(t)?.map((item: FiltrosProps, index: number) => (
            <button
              key={index}
              className={`flex flex-col items-center ${
                item.value === filtroAtivo
                  ? "text-black menuNavItemsActive"
                  : "text-black/20 menuNavItems"
              } hover:text-black text-xs transition-all`}
              type="button"
              onClick={() => {
                handleFiltroClick(item.value);
                queryClient.invalidateQueries({ queryKey: ["tarefas"] });
              }}
            >
              {item.label}
              <div className="borderLabel" />
            </button>
          ))}
        </div>

        <div>
          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setOpenFilters(!openFilters)}
              className="flex items-center gap-1 text-sm bg-azul-400 text-white px-2 rounded-md my-2"
            >
              <Filter size={14} />
              {t("common:filters")}
            </button>
            {openFilters && (
              <button
                type="button"
                onClick={() => {
                  setFiltroAtivo("hoje");
                  setCurrentPage(1);
                  setPayload({ period: "hoje" });
                }}
                className="flex items-center gap-1 text-sm bg-red-500 text-white px-2 rounded-md my-2"
              >
                <Trash2 size={14} />
                {t("common:clear")}
              </button>
            )}
          </div>

          {openFilters && (
            <div className="space-y-2">
              <Select
                placeholder={t("tasks.fields.creator")}
                onChange={(value: any) => {
                  setCurrentPage(1);
                  setPayload({ ...payload, id_user: value?.value });
                }}
                onInputChange={handleInputChangeUsers}
                isLoading={loadingUsers}
                options={formatUserData(users)}
                noOptionsMessage={() => t("common:type_to_search")}
                formatOptionLabel={(user: any) => (
                  <div className="flex gap-2 items-start">
                    <img
                      className="w-6 h-6 rounded-full"
                      src={user.image ?? ImageGeneric}
                      alt={user.label}
                    />
                    <span>{user.label}</span>
                  </div>
                )}
              />
              <Select
                placeholder={t("tasks.fields.responsible")}
                onChange={(value: any) => {
                  setCurrentPage(1);
                  setPayload({ ...payload, id_responsavel: value?.value });
                }}
                onInputChange={handleInputChangeUsers}
                isLoading={loadingUsers}
                options={formatUserData(users)}
                noOptionsMessage={() => t("common:type_to_search")}
                formatOptionLabel={(user: any) => (
                  <div className="flex gap-2 items-start">
                    <img
                      className="w-6 h-6 rounded-full"
                      src={user.image ?? ImageGeneric}
                      alt={user.label}
                    />
                    <span>{user.label}</span>
                  </div>
                )}
              />
            </div>
          )}
        </div>

        <div className="mt-3 min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
          {tarefas?.length === 0 && !isLoadingTasks && (
            <span className="block text-center text-sm">
              {t("tasks.no_tasks_found")}
            </span>
          )}

          {isLoadingTasks && (
            <>
              {Array.from({ length: 2 }).map((_, index) => (
                <div
                  key={index}
                  className="bg-[#EDEDED] rounded-3xl px-6 py-4 sm:flex justify-between items-center gap-4 animate-pulse"
                >
                  <div className="flex gap-4 pb-2">
                    <div className="w-6 h-6 bg-gray-300 rounded-full" />
                    <div className="w-full">
                      <div className="bg-gray-300 h-4 w-3/4 mb-2 rounded" />
                      <div className="bg-gray-300 h-3 w-1/2 rounded" />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-between">
                    <div className="flex sm:flex-col items-start gap-1">
                      <div className="flex gap-1 text-xs">
                        <div className="bg-gray-300 h-4 w-20 rounded" />
                        <div className="bg-gray-300 h-4 w-20 rounded" />
                      </div>
                      <div className="bg-gray-300 h-4 w-24 rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {isReimbursementFilter && tarefas.length > 0 && (
            <span className="block text-azul-400">
              {reimbursementLabel.toUpperCase()} ({tasksTotal})
            </span>
          )}
          {isReimbursementFilter &&
            tarefas.map((e: any) => (
              <CardTarefa
                key={e.id}
                tarefa={e}
                checkedTask={checkedTask}
                setTarefaSelecionada={setTarefaSelecionada}
              />
            ))}

          {!isReimbursementFilter && activeTasks?.length > 0 && (
            <span className="block text-[#FF0000]">
              {t("tasks.status.pending").toUpperCase()} (
              {filtroAtivo === "pendentes" ? tasksTotal : activeTasks.length})
            </span>
          )}
          {!isReimbursementFilter && activeTasks?.map((e: any) => (
            <CardTarefa
              key={e.id}
              tarefa={e}
              checkedTask={checkedTask}
              setTarefaSelecionada={setTarefaSelecionada}
            />
          ))}

          {!isReimbursementFilter && emAndamentoTasks?.length > 0 && (
            <span className="block text-azul-400">
              {t("tasks.status.in_progress").toUpperCase()} (
              {filtroAtivo === "emAndamento"
                ? tasksTotal
                : emAndamentoTasks.length}
              )
            </span>
          )}
          {!isReimbursementFilter && emAndamentoTasks?.map((e: any) => (
            <CardTarefa
              key={e.id}
              tarefa={e}
              checkedTask={checkedTask}
              setTarefaSelecionada={setTarefaSelecionada}
            />
          ))}

          {!isReimbursementFilter && tarefasCompletas?.length > 0 && (
            <span className="block text-azul-400">
              {`${t("tasks.status.completed").toUpperCase()} (${
                filtroAtivo === "concluidas"
                  ? tasksTotal
                  : tarefasCompletas.length
              })`}
            </span>
          )}
          {!isReimbursementFilter && tarefasCompletas?.map((e: any) => (
            <CardTarefa
              key={e.id}
              tarefa={e}
              checkedTask={checkedTask}
              setTarefaSelecionada={setTarefaSelecionada}
            />
          ))}

          {isPaginatedFilter && tasksTotal > SIDEBAR_TASKS_PER_PAGE && (
            <div className="flex items-center justify-between gap-2 py-3 text-xs">
              <button
                type="button"
                className="rounded-md border border-azul-400 px-3 py-1 text-azul-400 disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                disabled={currentPage === 1 || isFetching}
              >
                Anterior
              </button>
              <span className="text-cinza-300">
                Página {currentPage} de {lastPage}
              </span>
              <button
                type="button"
                className="rounded-md border border-azul-400 px-3 py-1 text-azul-400 disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() =>
                  setCurrentPage((page) => Math.min(page + 1, lastPage))
                }
                disabled={currentPage === lastPage || isFetching}
              >
                Próxima
              </button>
            </div>
          )}
        </div>

        <div className="space-y-2 mt-4" />

        {tarefaSelecionada && (
          <TarefaView
            task={tarefaSelecionada}
            openEdit={openEdit}
            getTasks={reloadTasks}
            setTaskSelected={setTarefaSelecionada}
          />
        )}
      </div>

      {openExpansao && (
        <ModalExpansao
          close={() => setOpenExpansao(false)}
          setPayload={setPayload}
          setFiltroAtivo={setFiltroAtivo}
          filtroAtivo={filtroAtivo}
          dataValue={tarefas}
          isPending={isLoadingTasks}
          checkedTask={checkedTask}
          setTaskSelected={setTarefaSelecionada}
          openEdit={openEdit}
        />
      )}
    </>
  );
}
